import { createContext, memo, useCallback, useContext, useMemo } from 'react';

import { Button } from '../Button';
import { cn } from '../../lib/styles';
import { Loader } from '../Loader';

interface TabsContextType {
  activeTab: number;
  setActiveTab: (index: number) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);

  if (!context) throw new Error('Tab components must be used within a Tabs component');

  return context;
}

export interface TabData<T extends string = string> {
  label: string;
  value?: T;
  disabled?: boolean;
  loading?: boolean;
  rightIcon?: React.ReactNode;
  tooltip?: string;
}

interface TabProps {
  children: React.ReactNode;
  index?: number;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  className?: string;
  rightIcon?: React.ReactNode;
  onClick?: () => void;
}

export const TabBase = ({
  children,
  index = 0,
  className,
  disabled,
  loading,
  tooltip,
  rightIcon,
  onClick,
}: TabProps) => {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === index;

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      (event.currentTarget as HTMLButtonElement).blur();
      onClick?.();
    },
    [onClick],
  );

  const isActiveAndNotDisabled = isActive && !disabled;

  return (
    <div className={cn('pb-2 relative')}>
      <Button
        variant="tertiary"
        className={cn('px-2', className)}
        disabled={disabled}
        tooltip={tooltip}
        onClick={handleClick}
        size="md"
      >
        {children}
        {loading ? <Loader className={cn('ml-1 size-4', disabled && 'opacity-50')} /> : null}
        {!loading && rightIcon ? rightIcon : null}
      </Button>

      <div
        className={cn(
          'absolute bottom-0 left-2 w-[calc(100%-16px)] h-[1.5px] bg-bg-inter-primary-default transition-all',
          {
            'opacity-0': !isActiveAndNotDisabled,
            'opacity-100': isActiveAndNotDisabled,
          },
        )}
      />
    </div>
  );
};

export const Tab = memo(TabBase);

export interface TabsProps<T extends string = string> {
  id?: string;
  children?: React.ReactNode;
  data?: T[] | TabData[];
  className?: string;
  value?: T;
  onChange?: (tab: T) => void;
}

const TabsBase = <T extends string = string>({ id, children, data, className, value, onChange }: TabsProps<T>) => {
  const activeTab = useMemo(() => {
    if (value)
      return data?.findIndex((item) => (typeof item === 'string' ? item === value : item.value === value)) || 0;

    return 0;
  }, [data, value]);

  const setActiveTab = useCallback(
    (index: number) => {
      const tab = data?.[index];

      if (typeof tab === 'string') return onChange?.(tab);

      if (!tab || !tab.value) return;

      onChange?.(tab.value as T);
    },
    [data, onChange],
  );

  const content = useMemo(() => {
    if (data) {
      return (
        <>
          {data.map((item, index) => {
            const label = typeof item === 'string' ? item : item.label;

            return (
              <Tab
                key={index}
                index={index}
                onClick={() => setActiveTab(index)}
                disabled={typeof item === 'string' ? false : item.disabled}
                loading={typeof item === 'string' ? false : item.loading}
                rightIcon={typeof item === 'string' ? undefined : item.rightIcon}
                tooltip={typeof item === 'string' ? undefined : item.tooltip}
              >
                {label}
              </Tab>
            );
          })}
        </>
      );
    }

    return children;
  }, [data, children, setActiveTab]);

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div id={id} className={cn('w-full flex px-4', className)}>
        {content}
      </div>
    </TabsContext.Provider>
  );
};

export const Tabs = memo(TabsBase) as typeof TabsBase;
